import { Option } from '@joor/design-system'

import { formatNameWithCode } from 'utils/formatters'

import { OrderDetailsQuery } from './OrderDetailsWidget'

// #region FORM Types, constants and utils for the useOrderDetailsForm hook

export const FormIds = {
  OrderType: 'orderType',
  OrderEvent: 'orderEvent',
  Warehouse: 'orderWarehouse',
  SeasonName: 'seasonName',
  SeasonCode: 'seasonCode',
  SeasonYear: 'seasonYear',
} as const

type FormIdsType = (typeof FormIds)[keyof typeof FormIds]

type DateRange = {
  startDate: Date | null
  endDate: Date | null
}

export type FormValues = {
  [FormIds.OrderType]: Option | null
  [FormIds.OrderEvent]: { value: string | null; label: string } | null
  [FormIds.Warehouse]: Option | null
  [FormIds.SeasonName]: string | null
  [FormIds.SeasonCode]: string | null
  [FormIds.SeasonYear]: number | null
}

type HandleChangeValueParam = Option | string | DateRange | number | null

export type HandleChangeFunction = (
  id: FormIdsType,
  value: HandleChangeValueParam,
) => void

const INITIAL_VALUES: FormValues = {
  [FormIds.OrderType]: null,
  [FormIds.OrderEvent]: null,
  [FormIds.Warehouse]: null,
  [FormIds.SeasonName]: null,
  [FormIds.SeasonCode]: null,
  [FormIds.SeasonYear]: null,
}

export const formUtils = {
  getFormValues: function (details?: OrderDetailsQuery) {
    if (!details) {
      return INITIAL_VALUES
    }

    const { types, event, warehouses, season } = details

    const orderType = types?.[0] ?? null

    const warehouse = warehouses?.edges[0]?.node ?? null

    return {
      [FormIds.OrderType]: orderType
        ? {
            value: orderType.id,
            label: formatNameWithCode(orderType.name, orderType.code),
          }
        : null,
      [FormIds.OrderEvent]: event
        ? { value: event.id, label: formatNameWithCode(event.name, event.code) }
        : null,
      [FormIds.Warehouse]: warehouse
        ? {
            value: warehouse.id,
            label: formatNameWithCode(warehouse.name, warehouse.code),
          }
        : null,
      [FormIds.SeasonName]: season?.name ?? null,
      [FormIds.SeasonCode]: season?.code ?? null,
      [FormIds.SeasonYear]: season?.year ?? null,
    }
  },
} as const
// #endregion

// #region UPDATE MUTATIONS Types, constants and utils for the useUpdateOrderDetails hook
// Types
export type PayloadParams = {
  updateOrderInfoPayload: UpdateOrderInfoPayload
  updateOrderDeliveryInfoPayload: UpdateOrderDeliveryInfoPayload
}

type UpdateOrderInfoPayload = {
  typeId?: string
  eventId?: string | null
}

type UpdateOrderDeliveryInfoPayload = {
  warehouseId?: string
  season?: {
    name: string
    code: string
    year: number
  }
}

export type UpdateOrderDetailsResult = {
  status: 'success' | 'error' | 'warning'
  title: string
  message: string
}

const payloadLiteralFieldsMap = {
  typeId: 'Order Type',
  eventId: 'Event',
  warehouseId: 'Warehouse',
  season: 'Season',
} as const

type PayloadLiteralFieldsMap = typeof payloadLiteralFieldsMap

type PayloadLiteralFields = keyof PayloadLiteralFieldsMap

export type ResultsAggregation = Record<
  string,
  { failed: boolean; fields: string[] }
>

// Inner Utilities
function formatEventAndTypeInput(
  payload: UpdateOrderInfoPayload,
  orderId: string,
) {
  if (Object.keys(payload).length === 0) return null

  return {
    orderIdentifiers: {
      orderIds: [orderId],
    },
    orderDetails: payload,
  }
}

function formatDeliveryInfoInput(
  payload: UpdateOrderDeliveryInfoPayload,
  orderId: string,
) {
  if (Object.keys(payload).length === 0) return null

  return {
    orderId,
    orderDeliveryInfo: payload,
  }
}

function getLiteralFieldsPerPayloadType(
  payload: UpdateOrderInfoPayload | UpdateOrderDeliveryInfoPayload,
): string[] {
  const temp = payload as Record<string, string | null | undefined>
  return Object.keys(temp)
    .filter((key) => !!temp[key])
    .map((key) => payloadLiteralFieldsMap[key as PayloadLiteralFields])
}

// Public Utilities
export const updateDetailsUtils = {
  formatMutationInputs: function (payload: PayloadParams, orderId: string) {
    const inputEventAndType = formatEventAndTypeInput(
      payload.updateOrderInfoPayload,
      orderId,
    )

    const inputDeliveryInfo = formatDeliveryInfoInput(
      payload.updateOrderDeliveryInfoPayload,
      orderId,
    )

    return {
      inputEventAndType,
      inputDeliveryInfo,
    }
  },
  initializeResultsAggregation: function (
    payload: PayloadParams,
  ): ResultsAggregation {
    return {
      updateOrderInfo: {
        failed: false,
        fields: getLiteralFieldsPerPayloadType(payload.updateOrderInfoPayload),
      },
      updateOrderDeliveryInfo: {
        failed: false,
        fields: getLiteralFieldsPerPayloadType(
          payload.updateOrderDeliveryInfoPayload,
        ),
      },
    }
  },
  successUpdateResult: {
    status: 'success',
    title: 'Order details have been successfully updated',
    message: 'Order details updated',
  } as UpdateOrderDetailsResult,

  errorUpdateResult: {
    status: 'error',
    title: 'An unexpected server error occurred',
    message: 'Order details failed to update',
  } as UpdateOrderDetailsResult,

  warningUpdateResult: function (fields: string): UpdateOrderDetailsResult {
    return {
      status: 'warning',
      title: 'Order details partially updated',
      message: `${fields} failed to update`,
    }
  },
  computeResult: function ({
    resultsAgg,
    mutationCount,
  }: {
    resultsAgg: ResultsAggregation
    mutationCount: number
  }): UpdateOrderDetailsResult {
    const failedMutations = Object.keys(resultsAgg).filter(
      (mutationName) => resultsAgg[mutationName].failed,
    )

    // all mutations succeeded
    if (failedMutations.length === 0) {
      return this.successUpdateResult
    }

    // all mutations failed
    if (failedMutations.length === mutationCount) {
      return this.errorUpdateResult
    }

    // some mutation/s failed
    const failedMutationsFields = failedMutations.reduce(
      (acc, mutationName) => [...acc, ...resultsAgg[mutationName].fields],
      [] as string[],
    )
    const fieldsStr = failedMutationsFields.join(', ')

    return this.warningUpdateResult(fieldsStr)
  },
  createMutationsPayload: function ({
    formValues,
    details,
  }: {
    formValues: FormValues
    details: OrderDetailsQuery
  }) {
    const typeId = formValues[FormIds.OrderType]?.value
    const eventId = formValues[FormIds.OrderEvent]?.value
    const warehouseId = formValues[FormIds.Warehouse]?.value
    const seasonName = formValues[FormIds.SeasonName]
    const seasonCode = formValues[FormIds.SeasonCode]
    const seasonYear = formValues[FormIds.SeasonYear]

    // ✅ Checking if there are changes to update for payload type
    const updateOrderInfoPayload: UpdateOrderInfoPayload = {
      ...(typeId !== details?.types?.[0]?.id && {
        typeId,
      }),
      ...(eventId !== details?.event?.id && {
        eventId,
      }),
    }

    const shouldUpdateSeason =
      seasonName !== details?.season?.name ||
      seasonCode !== details?.season?.code ||
      seasonYear !== details?.season?.year

    const updateOrderDeliveryInfoPayload: UpdateOrderDeliveryInfoPayload = {
      ...(warehouseId !== details?.warehouses?.edges[0]?.node?.id && {
        warehouseId,
      }),
      ...(shouldUpdateSeason && {
        season: {
          name: seasonName!,
          code: seasonCode!,
          year: seasonYear!,
        },
      }),
    }

    return {
      updateOrderInfoPayload,
      updateOrderDeliveryInfoPayload,
    }
  },
} as const
// #endregion
